import { useRef, useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
	DEFAULT_TEMPLATE_CONFIGURATION,
	usePdfTemplateConfigurationContext,
} from "../../context/PdfTemplateConfigurationContext";
import { REMINDER_PLACEHOLDERS } from "../debitoren/mahnwesen/utils/reminder-placeholders";
import { ReminderTemplateType } from "../debitoren/mahnwesen/utils/reminder-stage";

interface ReminderLetterSettingsProps {
	pdfTemplateType: ReminderTemplateType;
}

interface LetterText {
	title: string;
	bodyText: string;
}

export const ReminderLetterSettings: React.FC<ReminderLetterSettingsProps> = ({
	pdfTemplateType,
}) => {
	const { pdfTemplateConfiguration, updatePdfTemplateConfiguration } =
		usePdfTemplateConfigurationContext();
	const [draft, setDraft] = useState<LetterText | null>(null);
	const [saving, setSaving] = useState(false);
	const bodyRef = useRef<HTMLTextAreaElement>(null);
	const saved: LetterText = {
		title: pdfTemplateConfiguration?.[pdfTemplateType].title ?? "",
		bodyText: pdfTemplateConfiguration?.[pdfTemplateType].bodyText ?? "",
	};
	const letter = draft ?? saved;

	const setLetter = (changes: Partial<LetterText>) =>
		setDraft({ ...letter, ...changes });

	const handleInsertPlaceholder = (token: string) => {
		const start = bodyRef.current?.selectionStart ?? letter.bodyText.length;
		const end = bodyRef.current?.selectionEnd ?? letter.bodyText.length;
		setLetter({
			bodyText:
				letter.bodyText.slice(0, start) +
				token +
				letter.bodyText.slice(end),
		});
	};

	const handleSave = async (next: LetterText) => {
		setSaving(true);
		const success = await updatePdfTemplateConfiguration(
			pdfTemplateType,
			next
		);
		setSaving(false);
		if (success) setDraft(null);
	};

	const handleReset = () =>
		handleSave({
			title: DEFAULT_TEMPLATE_CONFIGURATION[pdfTemplateType].title,
			bodyText: DEFAULT_TEMPLATE_CONFIGURATION[pdfTemplateType].bodyText,
		});

	return (
		<div
			className="flex flex-col gap-2 pb-4"
			data-testid={`reminder-letter-settings-${pdfTemplateType}`}
		>
			<Label htmlFor={`reminder-letter-title-${pdfTemplateType}`}>
				Titel
			</Label>
			<Input
				id={`reminder-letter-title-${pdfTemplateType}`}
				value={letter.title}
				onChange={(event) => setLetter({ title: event.target.value })}
				aria-label="Titel des Mahnbriefs"
				data-testid={`reminder-letter-title-${pdfTemplateType}`}
			/>
			<Label htmlFor={`reminder-letter-body-${pdfTemplateType}`}>
				Brieftext
			</Label>
			<Textarea
				id={`reminder-letter-body-${pdfTemplateType}`}
				ref={bodyRef}
				value={letter.bodyText}
				rows={10}
				onChange={(event) =>
					setLetter({ bodyText: event.target.value })
				}
				aria-label="Text des Mahnbriefs"
				data-testid={`reminder-letter-body-${pdfTemplateType}`}
			/>
			<div className="flex flex-wrap gap-1">
				{REMINDER_PLACEHOLDERS.map(({ token, label }) => (
					<Button
						key={token}
						type="button"
						variant="outline"
						size="sm"
						className="h-7 px-2 text-xs"
						onClick={() => handleInsertPlaceholder(token)}
						aria-label={`Platzhalter ${label} einfügen`}
						title={label}
					>
						{token}
					</Button>
				))}
			</div>
			<div className="flex justify-end gap-2">
				<Button
					type="button"
					variant="outline"
					size="sm"
					disabled={saving}
					onClick={handleReset}
					aria-label="Brieftext auf Standard zurücksetzen"
					data-testid={`reminder-letter-reset-${pdfTemplateType}`}
				>
					Standard wiederherstellen
				</Button>
				<Button
					type="button"
					size="sm"
					disabled={!draft || saving}
					onClick={() => handleSave(letter)}
					aria-label="Brieftext speichern"
					data-testid={`reminder-letter-save-${pdfTemplateType}`}
				>
					Speichern
				</Button>
			</div>
		</div>
	);
};
